import { LifetimeOfferCountdown } from '@/hooks/use-lifetime-offer';
import { Fragment } from 'react';

/** Zero-pads to two digits so the countdown does not jitter in width. */
const pad = (value: number) => String(value).padStart(2, '0');

/**
 * The offer countdown, weighted so it reads as urgent at a glance instead of
 * blending into the copy around it.
 *
 * Both sizes are aria-hidden: a per-second countdown would flood a screen
 * reader, so every caller announces the deadline in nearby static text
 * instead.
 */
export default function LifetimeCountdownComponent({
  countdown,
  size = 'lg'
}: {
  countdown: LifetimeOfferCountdown;
  /**
   * `sm` is a bold inline 22:47:15; `md` is inline white digit chips
   * 23 : 59 : 41; `lg` is boxed segments with labels.
   */
  size?: 'sm' | 'md' | 'lg';
}) {
  if (size === 'sm') {
    return (
      <span
        className="font-bold tabular-nums"
        aria-hidden="true"
        suppressHydrationWarning
      >
        {pad(countdown.hours)}:{pad(countdown.minutes)}:{pad(countdown.seconds)}
      </span>
    );
  }

  if (size === 'md') {
    const values = [countdown.hours, countdown.minutes, countdown.seconds];

    return (
      <span
        className="inline-flex items-center gap-1 align-middle"
        aria-hidden="true"
      >
        {values.map((value, index) => (
          <Fragment key={index}>
            {index > 0 && <span className="text-xs font-bold">:</span>}
            <span
              className="rounded-md bg-white px-1.5 py-0.5 text-sm font-bold tabular-nums shadow-sm"
              suppressHydrationWarning
            >
              {pad(value)}
            </span>
          </Fragment>
        ))}
      </span>
    );
  }

  const segments = [
    { value: countdown.hours, unit: 'hrs' },
    { value: countdown.minutes, unit: 'min' },
    { value: countdown.seconds, unit: 'sec' }
  ];

  return (
    <div
      className="flex items-start justify-center gap-2 sm:gap-3"
      aria-hidden="true"
    >
      {segments.map((segment, index) => (
        <Fragment key={segment.unit}>
          {index > 0 && (
            <span className="pt-5 text-lg font-bold opacity-20 sm:pt-6">:</span>
          )}
          <span className="flex w-16 flex-col items-center gap-2 sm:w-[72px]">
            <span
              className="w-full rounded-2xl bg-[#FAF0D3] py-3 text-center text-2xl font-bold tabular-nums sm:py-4 sm:text-3xl"
              suppressHydrationWarning
            >
              {pad(segment.value)}
            </span>
            <span className="text-xs uppercase tracking-widest opacity-50">
              {segment.unit}
            </span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}
